import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getPost, updatePost, getUploadUrl, uploadFileToS3 } from '../services/api';

const TITLE_MAX = 64;
const animalTypes = ['horse', 'sheep', 'goat', 'chicken', 'cattle', 'pig'];

function EditListing() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [existing, setExisting] = useState([]); // current image URLs to keep
  const [files, setFiles] = useState([]); // newly added files
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getPost(postId)
      .then((p) => {
        setForm({
          title: p.title || '',
          details: p.details || p.description || '',
          animal_type: p.animal_type || 'horse',
          price: p.price ?? '',
          location: p.location || ''
        });
        setExisting(p.images || []);
      })
      .catch((err) => {
        console.error(err);
        setError('Could not load this listing (you may not own it).');
      });
  }, [postId]);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const removeExisting = (url) => setExisting((prev) => prev.filter((u) => u !== url));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const { uploadUrl, publicUrl } = await getUploadUrl(
          postId,
          file.name,
          file.type || 'application/octet-stream'
        );
        await uploadFileToS3(uploadUrl, file);
        newUrls.push(publicUrl);
      }
      await updatePost(postId, { ...form, images: [...existing, ...newUrls] });
      navigate(`/posts/${postId}`);
    } catch (err) {
      console.error('Update failed:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (error && !form) return <div className="alert alert-danger">{error}</div>;
  if (!form) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/my-listings" className="btn btn-sm btn-outline-secondary mb-3">
        ← My Listings
      </Link>
      <h1 className="mb-4">Edit Listing</h1>
      {error && <div className="alert alert-danger">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Title *</label>
          <input
            type="text"
            name="title"
            className="form-control"
            value={form.title}
            onChange={change}
            maxLength={TITLE_MAX}
            required
          />
          <div className="hl-title-counter text-end">
            {form.title.length}/{TITLE_MAX}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Details</label>
          <textarea
            name="details"
            className="form-control"
            rows="4"
            value={form.details}
            onChange={change}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Animal Type *</label>
          <select
            name="animal_type"
            className="form-select"
            value={form.animal_type}
            onChange={change}
            required
          >
            {animalTypes.map((t) => (
              <option key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Price (USD)</label>
          <input
            type="number"
            name="price"
            className="form-control"
            value={form.price}
            onChange={change}
            min="0"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Location</label>
          <input
            type="text"
            name="location"
            className="form-control"
            value={form.location}
            onChange={change}
          />
        </div>

        {existing.length > 0 && (
          <div className="mb-3">
            <label className="form-label">Current Photos</label>
            <div className="hl-thumb-grid">
              {existing.map((url) => (
                <div key={url} className="position-relative">
                  <img src={url} alt="" className="hl-thumb" />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 py-0"
                    onClick={() => removeExisting(url)}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="form-label">Add Photos</label>
          <input
            type="file"
            className="form-control"
            accept="image/*"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}

export default EditListing;
